import { t as tr, tk } from "../../lib/i18n";
import { LearnImage } from "../../ui/LearnImage";
import { Mascot } from "../../ui/Art";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { quizStars } from "@jomcerdik/shared";
import { api, errorMessage } from "../../lib/api";
import { Alert } from "../../components/Form";
import { Card, Screen, Stars, StudyGate, useLang } from "./kit";
import { guide } from "../../world/catalog";

type Q = { id: string; difficulty: string; imageId?: string; spCode: string; stemMs: string; stemEn: string; optionsMs: string[]; optionsEn: string[] };
type Result = {
  score: number; max: number; percent: number; tp: number;
  results: { q: string; choice: number; answer: number; correct: boolean; explanationMs: string; explanationEn: string }[];
};

export function QuizPage() {
  return (
    <Screen>
      <StudyGate>
        <Inner />
      </StudyGate>
    </Screen>
  );
}

function Inner() {
  const { topicId = "" } = useParams();
  const { pick, lang } = useLang();
  const [qs, setQs] = useState<Q[] | null>(null);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  useEffect(() => { if (result) guide(result.percent >= 50 ? "correct" : "wrong", result.percent >= 50 ? tk("Hebat! Teruskan usaha.") : tk("Cuba lagi. Kamu boleh belajar daripada jawapan tadi.")); }, [result]);

  useEffect(() => {
    setQs(null); setI(0); setPicked({}); setResult(null);
    api<{ questions: Q[] }>(`/api/learn/unit/${topicId}/quiz`).then((r) => setQs(r.questions)).catch((e) => setError(errorMessage(e)));
  }, [topicId, round]);

  async function finish() {
    setBusy(true);
    try {
      const answers = qs!.map((q) => ({ q: q.id, c: picked[q.id] ?? -1 })).filter((a) => a.c >= 0);
      setResult(await api<Result>(`/api/learn/unit/${topicId}/quiz`, { method: "POST", json: { answers } }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <Alert kind="error">{error}</Alert>;
  if (!qs) return <p className="text-sm text-slate-500">…</p>;

  const opts = (q: Q) => (lang === "en" && q.optionsEn?.length === 4 ? q.optionsEn : q.optionsMs?.length === 4 ? q.optionsMs : q.optionsEn);

  if (result) {
    const stars = quizStars(result.score, result.max);
    const byId = new Map(result.results.map((r) => [r.q, r]));
    return (
      <>
        <Card className="text-center">
          <Mascot pose={stars >= 2 ? "boy-cheer" : stars === 1 ? "girl-happy" : "boy-think"} className="mx-auto h-32 w-32" />
          <p className="mt-2 text-3xl font-extrabold text-slate-900">{result.score}/{result.max}</p>
          <Stars n={stars} size="text-3xl" />
          <p className="mt-1 text-sm text-slate-500">
            {stars >= 2 ? tr("Hebat! Teruskan usaha.") : tr("Cuba lagi. Baca semula nota dan jawab sekali lagi.")}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <button type="button" onClick={() => setRound((r) => r + 1)} className="rounded-full bg-violet-500 px-5 py-2 text-sm font-bold text-white">{tr("Cuba lagi")}</button>
            <Link to={`/belajar/unit/${topicId}`} className="rounded-full bg-card px-5 py-2 text-sm font-bold text-slate-700 ring-1 ring-black/10">{tr("Ke nota")}</Link>
          </div>
        </Card>
        <h2 className="mb-2 mt-6 font-extrabold text-slate-900">{tr("Semakan jawapan")}</h2>
        <ol className="space-y-3">
          {qs.map((q, k) => {
            const r = byId.get(q.id);
            const o = opts(q);
            return (
              <li key={q.id}>
                <Card className={r?.correct ? "ring-emerald-200" : "ring-rose-200"}>
                  <p className="text-sm font-bold text-slate-900">{k + 1}. {pick(q.stemMs, q.stemEn)}</p>
                  {r && (
                    <>
                      <p className={`mt-2 text-sm font-semibold ${r.correct ? "text-emerald-600" : "text-rose-600"}`}>
                        {r.correct ? `✔ ${o[r.answer]}` : `✘ ${o[r.choice] ?? "-"} → ✔ ${o[r.answer]}`}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{pick(r.explanationMs, r.explanationEn)}</p>
                    </>
                  )}
                  {!r && <p className="mt-2 text-sm text-slate-400">{tr("Tidak dijawab")}</p>}
                </Card>
              </li>
            );
          })}
        </ol>
      </>
    );
  }

  const q = qs[i]!;
  const choice = picked[q.id];
  const answered = Object.keys(picked).length;
  const o = opts(q);
  return (
    <>
      <div className="mb-5 rounded-3xl bg-gradient-to-r from-rose-400 to-pink-500 px-4 py-3 text-white shadow-md">
        <div className="flex items-center justify-between gap-2">
          <Link to={`/belajar/unit/${topicId}`} aria-label={tr("Tutup")} className="text-xl">✕</Link>
          <p className="text-sm font-bold">{tr("Soalan {i} daripada {n}", { i: i + 1, n: qs.length })}</p>
        </div>
        <div className="mt-2 flex gap-1">
          {qs.map((x, k) => <span key={x.id} className={`h-1.5 flex-1 rounded-full ${k === i ? "bg-white" : picked[x.id] !== undefined ? "bg-white/70" : "bg-white/30"}`} />)}
        </div>
      </div>

      <Card>
        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">
          {q.difficulty === "tinggi" ? tr("KBAT") : q.difficulty === "sederhana" ? tr("Sederhana") : tr("Mudah")}
        </p>
        <LearnImage id={q.imageId} className="mx-auto mb-3 aspect-[4/3] w-full max-w-sm" />
        <p className="text-lg font-extrabold leading-snug text-slate-900">{pick(q.stemMs, q.stemEn)}</p>
      </Card>

      {/* Answers in a 2 × 2 grid (A B / C D), like the option layout in exam papers. */}
      <div className="mt-4 grid grid-cols-2 gap-3" role="radiogroup">
        {o.map((text, k) => (
          <button key={k} type="button" role="radio" aria-checked={choice === k} onClick={() => setPicked((p) => ({ ...p, [q.id]: k }))}
            className={`flex min-h-[5.5rem] flex-col items-center justify-center gap-2 rounded-2xl border-2 bg-card p-3 text-center text-sm font-semibold leading-snug shadow-sm transition sm:text-base ${choice === k ? "border-violet-500 bg-violet-50 text-violet-800" : "border-transparent text-slate-800 ring-1 ring-black/5"}`}>
            <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-extrabold ${choice === k ? "bg-violet-500 text-white" : "bg-slate-100 text-slate-500"}`}>{"ABCD"[k]}</span>
            <span className="break-words">{text}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 flex gap-3">
        <button type="button" disabled={i === 0} onClick={() => setI(i - 1)} className="rounded-full bg-card px-5 py-3 text-sm font-bold text-slate-600 ring-1 ring-black/10 disabled:opacity-40">←</button>
        {i + 1 < qs.length ? (
          <button type="button" disabled={choice === undefined} onClick={() => setI(i + 1)}
            className="flex-1 rounded-full bg-violet-500 py-3 text-sm font-extrabold text-white disabled:opacity-40">{tr("Seterusnya")} →</button>
        ) : (
          <button type="button" disabled={busy || answered === 0} onClick={finish}
            className="flex-1 rounded-full bg-emerald-500 py-3 text-sm font-extrabold text-white disabled:opacity-40">
            {busy ? "…" : `${tr("Hantar")} (${answered}/${qs.length})`}
          </button>
        )}
      </div>
    </>
  );
}

